import { computed, toValue, type ComputedRef, type MaybeRefOrGetter } from 'vue';

import { Breakpoints, BREAKPOINTS, BreakpointsWithDefault, isBreakpointKey } from '../utils';

import { useBreakpointMediaQueries } from './use-breakpoint-media-queries';

/**
 * All breakpoints including base ('').
 * Index mapping: [0]='' [1]='xs' [2]='sm' [3]='md' [4]='lg' [5]='xl' [6]='2xl' [7]='3xl'
 */
const ALL_BREAKPOINTS = ['', ...BREAKPOINTS] as const;

/**
 * A responsive value that can be:
 * - A direct value (T)
 * - An object with breakpoint keys including '' for base ({ '': T, md: T })
 * - An array where index maps to breakpoint ([baseValue, xsValue, smValue, ...])
 *
 * Array values support inheritance: null/undefined values inherit from the previous breakpoint.
 *
 * @example
 * ```ts
 * // Direct value
 * const value: ResponsiveValue<boolean> = true;
 *
 * // Object with breakpoint keys
 * const value: ResponsiveValue<boolean> = { '': true, md: false };
 *
 * // Array (index-based)
 * const value: ResponsiveValue<boolean> = [true, null, null, false]; // base=true, md=false
 * ```
 */
export type ResponsiveValue<T> = T | Partial<Record<BreakpointsWithDefault, T>> | (T | null | undefined)[];

/**
 * Check if value is an array (responsive array format).
 */
export function isResponsiveArray<T>(value: unknown): value is (T | null | undefined)[] {
  return Array.isArray(value);
}

/**
 * Check if value is a responsive object (has breakpoint keys including '').
 */
export function isResponsiveValue<T>(value: ResponsiveValue<T>): value is Partial<Record<BreakpointsWithDefault, T>> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return false;
  }

  const keys = Object.keys(value);

  return keys.length > 0 && keys.every((k) => k === '' || isBreakpointKey(k));
}

/**
 * Normalizes a responsive value to object format.
 * - Primitives: returned as-is
 * - Objects: returned as-is
 * - Arrays: converted to object with breakpoint keys
 *
 * Array index mapping: [0]='' [1]='xs' [2]='sm' [3]='md' [4]='lg' [5]='xl' [6]='2xl' [7]='3xl'
 * Null/undefined values inherit from the previous breakpoint.
 *
 * @example
 * ```ts
 * normalizeResponsiveValue([{ x: '10%' }, null, null, { x: '20%' }])
 * // Returns: { '': { x: '10%' }, xs: { x: '10%' }, sm: { x: '10%' }, md: { x: '20%' }, ... }
 * ```
 */
export function normalizeResponsiveValue<T>(value: ResponsiveValue<T>): T | Partial<Record<BreakpointsWithDefault, T>> {
  if (!isResponsiveArray(value)) {
    return value;
  }

  const result: Partial<Record<BreakpointsWithDefault, T>> = {};
  let lastValue: T | undefined;

  for (let i = 0; i < ALL_BREAKPOINTS.length; i++) {
    const breakpoint = ALL_BREAKPOINTS[i];
    const item = i < value.length ? value[i] : undefined;

    if (item !== null && item !== undefined) {
      lastValue = item;
    }

    if (lastValue !== undefined) {
      result[breakpoint] = lastValue;
    }
  }

  return result;
}

/**
 * Get breakpoints (excluding '') defined in a responsive value.
 * Arrays are normalized first.
 */
export function getBreakpointsFromValue<T>(value: ResponsiveValue<T>): Breakpoints[] {
  const normalized = normalizeResponsiveValue(value);

  if (!isResponsiveValue(normalized)) {
    return [];
  }

  return BREAKPOINTS.filter((bp) => bp in normalized);
}

/**
 * Resolves a responsive value based on the current breakpoint.
 * Falls back to the closest smaller breakpoint if the current one is not defined.
 * Arrays are normalized to object format first.
 */
export function resolveResponsiveValue<T>(
  value: ResponsiveValue<T>,
  currentBreakpoint: Breakpoints | '',
  defaultValue: T,
): T {
  const normalized = normalizeResponsiveValue(value);

  if (!isResponsiveValue(normalized)) {
    return normalized;
  }

  // Handle base breakpoint ('')
  if (currentBreakpoint === '') {
    if ('' in normalized) {
      return normalized[''] as T;
    }

    // Fall back to first defined breakpoint
    return normalized[BREAKPOINTS[0]] ?? defaultValue;
  }

  const currentIndex = BREAKPOINTS.indexOf(currentBreakpoint);

  // Find the value at current breakpoint or fall back to closest smaller breakpoint
  for (let i = currentIndex; i >= 0; i--) {
    const bp = BREAKPOINTS[i];

    if (bp in normalized) {
      return normalized[bp] as T;
    }
  }

  // Fall back to base breakpoint
  if ('' in normalized) {
    return normalized[''] as T;
  }

  return defaultValue;
}

/**
 * Composable that resolves a responsive prop value based on the current breakpoint.
 *
 * @param value - A value that can be either a direct value, a responsive object with breakpoint keys, or an array
 * @param defaultValue - The default value to use when no breakpoint matches
 * @returns A computed ref that resolves to the correct value based on current viewport
 *
 * @example
 * ```ts
 * // Simple boolean
 * const showNav = useResponsiveProp(true, false);
 *
 * // Responsive object: true on md and up
 * const showNav = useResponsiveProp({ md: true }, false);
 *
 * // Responsive object with base: false on mobile, true on desktop
 * const showNav = useResponsiveProp({ '': false, lg: true }, false);
 *
 * // Responsive array: true on base, false on md and up
 * const showNav = useResponsiveProp([true, null, null, false], false);
 * ```
 */
export function useResponsiveProp<T>(value: MaybeRefOrGetter<ResponsiveValue<T>>, defaultValue: T): ComputedRef<T> {
  const resolvedValue = toValue(value);
  const normalized = normalizeResponsiveValue(resolvedValue);

  if (!isResponsiveValue(normalized)) {
    return computed(() => normalized);
  }

  const breakpointsToWatch = getBreakpointsFromValue(resolvedValue);
  const currentBreakpoint = useBreakpointMediaQueries(breakpointsToWatch);

  return computed(() => resolveResponsiveValue(toValue(value), currentBreakpoint.value, defaultValue));
}
